import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Loader2, CheckCircle, AlertTriangle, Lock } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL || '';

export default function AcceptInvitePage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { loginWithToken } = useAuth();

  // Read token from URL - try both 'token' and 't' params for robustness
  const token = searchParams.get('token') || searchParams.get('t') || '';

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [verifying, setVerifying] = useState(!!token);
  const [inviteEmail, setInviteEmail] = useState('');

  useEffect(() => {
    if (!token) {
      setError('No invitation token provided. Please use the link from your invitation email.');
      return;
    }
    // Verify the token is valid before showing the form
    const baseUrl = API ? `${API}/api` : '/api';
    axios.get(`${baseUrl}/users/verify-invite`, { params: { token: token.trim() } })
      .then(res => {
        if (res.data.valid) {
          setInviteEmail(res.data.email || '');
        } else {
          const reasons = {
            not_found: 'This invitation link is invalid. Please request a new invitation from your administrator.',
            already_used: `This invitation has already been used${res.data.email ? ` (${res.data.email})` : ''}. Please log in instead.`,
            expired: 'This invitation has expired. Please request a new invitation from your administrator.',
          };
          setError(reasons[res.data.reason] || 'Invalid invitation.');
        }
      })
      .catch(() => {
        // If verify endpoint doesn't exist (old backend), don't block — let submit handle it
      })
      .finally(() => setVerifying(false));
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);
    try {
      // Use relative URL as fallback if API is empty/undefined
      const baseUrl = API ? `${API}/api` : '/api';
      const res = await axios.post(`${baseUrl}/users/accept-invite`, {
        token: token.trim(),
        password,
      });

      setSuccess(true);
      toast.success('Account activated successfully!');

      if (res.data.access_token) {
        setTimeout(() => {
          loginWithToken(res.data.access_token, res.data.user);
          const route = res.data.user?.role === 'ATHLETE' ? '/athlete/wellness' : '/staff/roster';
          navigate(route);
        }, 1500);
      }
    } catch (err) {
      const detail = err.response?.data?.detail || 'Failed to activate account. Please try again or contact support.';
      setError(detail);
      toast.error(detail);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-zinc-950 flex items-center justify-center p-4" data-testid="accept-invite-page">
      <Card className="w-full max-w-md bg-zinc-900/80 border-zinc-800">
        <CardHeader className="text-center pb-2">
          <div className="mx-auto w-12 h-12 rounded-full bg-cyan-500/10 flex items-center justify-center mb-3">
            <Lock className="w-6 h-6 text-cyan-400" />
          </div>
          <CardTitle className="text-xl text-zinc-100">
            {success ? 'Account Activated' : 'Set Your Password'}
          </CardTitle>
          <p className="text-sm text-zinc-500 mt-1">
            {success
              ? 'Redirecting you to the app...'
              : inviteEmail
                ? `Setting up account for ${inviteEmail}`
                : 'Create a password to activate your account'}
          </p>
        </CardHeader>
        <CardContent>
          {success ? (
            <div className="flex flex-col items-center gap-3 py-4" data-testid="invite-success">
              <CheckCircle className="w-12 h-12 text-emerald-400" />
              <p className="text-zinc-300 text-sm">Your account is ready.</p>
              <Loader2 className="w-5 h-5 animate-spin text-zinc-500" />
            </div>
          ) : verifying ? (
            <div className="flex flex-col items-center gap-3 py-6" data-testid="invite-verifying">
              <Loader2 className="w-6 h-6 animate-spin text-zinc-400" />
              <p className="text-zinc-500 text-sm">Verifying invitation...</p>
            </div>
          ) : !token || (error && !password) ? (
            <div className="flex flex-col items-center gap-3 py-4" data-testid="invite-error-no-token">
              <AlertTriangle className="w-10 h-10 text-amber-400" />
              <p className="text-zinc-400 text-sm text-center">{error}</p>
              <Button variant="outline" onClick={() => navigate('/login')} className="mt-2 border-zinc-700">
                Go to Login
              </Button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <div className="p-3 rounded bg-rose-500/10 border border-rose-500/20 text-rose-400 text-sm" data-testid="invite-error">
                  {error}
                </div>
              )}
              <div>
                <Label className="text-zinc-400">Password</Label>
                <Input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Minimum 8 characters"
                  className="bg-zinc-950 border-zinc-800 mt-1"
                  data-testid="invite-password"
                  required
                  minLength={8}
                />
              </div>
              <div>
                <Label className="text-zinc-400">Confirm Password</Label>
                <Input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Re-enter your password"
                  className="bg-zinc-950 border-zinc-800 mt-1"
                  data-testid="invite-confirm-password"
                  required
                />
              </div>
              <Button type="submit" disabled={loading} className="w-full" data-testid="invite-submit-btn">
                {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CheckCircle className="w-4 h-4 mr-2" />}
                Activate Account
              </Button>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
